'use client';

import { AppInsightsErrorBoundary, ReactPlugin, AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { AppSession } from '@/auth';
import { ReactNode, useEffect } from 'react';
import { installHangReporting } from './app-insights-hang-reporting';
import { createRepeatPageViewFilter, maskBrowserTelemetryUrls, tagBrowserTelemetry } from './app-insights-telemetry';
import clientEnv from '@baseload/shared/constants/client-env';

let reactPlugin: ReactPlugin | undefined;
let applicationInsights: ApplicationInsights | undefined;

const initializeAppInsights = (connectionString: string) => {
  if (applicationInsights) throw new Error('App Insights already initialized');

  reactPlugin = new ReactPlugin();

  applicationInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      autoTrackPageVisitTime: true,
      enableCorsCorrelation: true,
      // Mapbox tile endpoints reject App Insights correlation headers like `request-id`.
      correlationHeaderExcludedDomains: ['api.mapbox.com'],
      enableUnhandledPromiseRejectionTracking: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      maxBatchSizeInBytes: 100000,
      maxBatchInterval: 15000,

      extensions: [reactPlugin]
    }
  });

  applicationInsights.loadAppInsights();

  // NEXT_PUBLIC_BUILD_ID is the commit SHA, set by the Docker build, so each item names its deploy.
  applicationInsights.addTelemetryInitializer(tagBrowserTelemetry(clientEnv.NEXT_PUBLIC_BUILD_ID));
  applicationInsights.addTelemetryInitializer(createRepeatPageViewFilter());
  applicationInsights.addTelemetryInitializer(maskBrowserTelemetryUrls);
};

/**
 * Reports an error that an error boundary caught before the App Insights boundary in the root layout
 * could see it, such as `app/error.tsx`. A no-op when App Insights is not configured.
 */
export function reportExceptionToAppInsights(error: Error) {
  applicationInsights?.trackException({ exception: error, severityLevel: 3 });
}

export default function AppInsights({
  session,
  connectionString,
  children
}: {
  session: AppSession;
  connectionString?: string;
  children: ReactNode;
}) {
  if (connectionString && !applicationInsights) initializeAppInsights(connectionString);

  useEffect(() => {
    if (!applicationInsights) return;

    if (!session?.user?.id) {
      applicationInsights.clearAuthenticatedUserContext();
      return;
    }

    applicationInsights.setAuthenticatedUserContext(String(session.user.id).replace(/[,;=\s|]/g, ''));
  }, [session?.user?.id]);

  // Declared after the user-context effect so a hang reported on load carries the user id.
  useEffect(() => {
    if (!applicationInsights) return;
    return installHangReporting(applicationInsights);
  }, []);

  if (!reactPlugin) return children;

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <AppInsightsErrorBoundary
        onError={() => {
          return (
            <div>
              <h2>Something went wrong!</h2>
            </div>
          );
        }}
        appInsights={reactPlugin}
      >
        {children}
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  );
}
